import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { 
  Edit, 
  Search, 
  PlusCircle,
  ChevronLeft,
  ChevronRight,
  Info,
  AlertTriangle
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
  DialogDescription,
  DialogClose,
  ScrollableDialogContent,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { apiClient } from '@/api/client';
import { supabase } from '@/integrations/supabase/client';

interface SuperAdminRestaurantsTabProps {
  restaurants: any[];
  organisations: any[];
}

const SuperAdminRestaurantsTab: React.FC<SuperAdminRestaurantsTabProps> = ({ 
  restaurants: initialRestaurants,
  organisations: initialOrganisations 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [orgFilter, setOrgFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('restaurants');
  
  // State for restaurants and organisations
  const [restaurants, setRestaurants] = useState<any[]>([]);
  const [organisations, setOrganisations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  
  // Dialog states
  const [editRestaurantDialogOpen, setEditRestaurantDialogOpen] = useState(false);
  const [addRestaurantDialogOpen, setAddRestaurantDialogOpen] = useState(false);
  const [editOrganisationDialogOpen, setEditOrganisationDialogOpen] = useState(false);
  const [addOrganisationDialogOpen, setAddOrganisationDialogOpen] = useState(false);
  
  // Selected items for editing
  const [selectedRestaurant, setSelectedRestaurant] = useState<any>(null);
  const [selectedOrganisation, setSelectedOrganisation] = useState<any>(null);
  
  // Form states
  const [restaurantForm, setRestaurantForm] = useState({
    restaurant_id: '',
    restaurant_name: '',
    organisation_id: '',
    address: '',
    email: '',
    phone: '',
    status: 'active',
    contact_person: '',
    logo_url: '',
    webhook_secret: '',
    api_key: '',
    commission_rate: '',
    ubereats_commission_rate: '',
    ubereats_markup: '',
    pickup_commission_rate: '',
    primary_color: '#4093ff',
    secondary_color: '',
    stripe_customer_id: '',
    opt_in_checkbox_id: '',
    implicit_consent_enabled: false,
    ordering_page_url: '',
    review_page_url: '',
    google_review_url: '',
    facebook_url: '',
    instagram_url: ''
  });
  
  const [organisationForm, setOrganisationForm] = useState({
    name: '',
    id: '',
    stripe_customer_id: ''
  });
  
  // Form validation states
  const [restaurantErrors, setRestaurantErrors] = useState({
    restaurant_name: ''
  });
  
  const [organisationErrors, setOrganisationErrors] = useState({
    name: '',
    id: ''
  });
  
  // Fetch restaurants and organisations
  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Calculate pagination range
      const startRow = (page - 1) * pageSize;
      const endRow = page * pageSize - 1;
      
      if (activeTab === 'restaurants') {
        // Build query for restaurants
        let query = supabase
          .from('restaurants_master')
          .select(`
            restaurant_id, 
            restaurant_name,
            organisation_id,
            address,
            status,
            email,
            phone,
            contact_person,
            organisations:organisation_id (
              id,
              name
            )
          `, { count: 'exact' })
          .order('restaurant_name')
          .range(startRow, endRow);
        
        // Apply search filter if provided
        if (searchQuery) {
          query = query.ilike('restaurant_name', `%${searchQuery}%`);
        }
        
        // Apply organisation filter if provided
        if (orgFilter === 'none') {
          query = query.is('organisation_id', null);
        } else if (orgFilter !== 'all') {
          query = query.eq('organisation_id', orgFilter);
        }
        
        // Apply status filter if provided
        if (statusFilter !== 'all') {
          query = query.eq('status', statusFilter);
        }
        
        // Execute the query
        const { data: restaurantData, error: restaurantError, count } = await query;
        
        if (restaurantError) {
          throw new Error(`Error fetching restaurants: ${restaurantError.message}`);
        }
        
        // Format the data to include organisation names
        const formattedRestaurants = (restaurantData || []).map(restaurant => ({
          ...restaurant,
          organisation_name: restaurant.organisations?.name || null
        }));
        
        setRestaurants(formattedRestaurants);
        setTotalItems(count || 0);
      } else {
        // Build query for organisations
        let query = supabase
          .from('organisations')
          .select(`
            id,
            name,
            restaurants:restaurants_master(restaurant_id, status)
          `, { count: 'exact' })
          .order('name')
          .range(startRow, endRow);
        
        // Apply search filter if provided
        if (searchQuery) {
          query = query.ilike('name', `%${searchQuery}%`);
        }
        
        // Execute the query
        const { data: organisationData, error: organisationError, count } = await query;
        
        if (organisationError) {
          throw new Error(`Error fetching organisations: ${organisationError.message}`);
        }
        
        // Format the data to include restaurant counts
        const formattedOrganisations = (organisationData || []).map(org => ({
          ...org,
          restaurant_count: org.restaurants?.length || 0,
          active_count: org.restaurants?.filter((r: any) => r.status === 'active').length || 0
        }));
        
        setOrganisations(formattedOrganisations);
        setTotalItems(count || 0);
      }
    } catch (err: any) {
      console.error(`Error fetching ${activeTab}:`, err);
      setError(err.message || `An error occurred while fetching ${activeTab}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Initial data fetch and refetch when dependencies change
  useEffect(() => {
    fetchData();
  }, [activeTab, page, pageSize, searchQuery, orgFilter, statusFilter]);
  
  // Reset page when tab or filters change
  useEffect(() => {
    setPage(1);
  }, [activeTab, searchQuery, orgFilter, statusFilter]);
  
  // Reset form when opening add dialogs
  useEffect(() => {
    if (addRestaurantDialogOpen) {
      setRestaurantForm({
        restaurant_id: '',
        restaurant_name: '',
        organisation_id: '',
        address: '',
        email: '',
        phone: '',
        status: 'active',
        contact_person: '',
        logo_url: '',
        webhook_secret: '',
        api_key: '',
        commission_rate: '',
        ubereats_commission_rate: '',
        ubereats_markup: '',
        pickup_commission_rate: '',
        primary_color: '#4093ff',
        secondary_color: '',
        stripe_customer_id: '',
        opt_in_checkbox_id: '',
        implicit_consent_enabled: false,
        ordering_page_url: '',
        review_page_url: '',
        google_review_url: '',
        facebook_url: '',
        instagram_url: ''
      });
      setRestaurantErrors({ restaurant_name: '' });
    }
  }, [addRestaurantDialogOpen]);
  
  useEffect(() => {
    if (addOrganisationDialogOpen) {
      setOrganisationForm({ name: '', id: '', stripe_customer_id: '' });
      setOrganisationErrors({ name: '', id: '' });
    }
  }, [addOrganisationDialogOpen]);
  
  // Set form data when selecting a restaurant for editing
  useEffect(() => {
    if (selectedRestaurant) {
      // Log the data we received from the API
      console.log('Setting form with restaurant data:', selectedRestaurant);
      
      // Prepare the form data with all fields, ensuring no nulls
      const formData = {
        restaurant_id: selectedRestaurant.restaurant_id || '',
        restaurant_name: selectedRestaurant.restaurant_name || '',
        organisation_id: selectedRestaurant.organisation_id || '',
        address: selectedRestaurant.address || '',
        email: selectedRestaurant.email || '',
        phone: selectedRestaurant.phone || '',
        status: selectedRestaurant.status || 'active',
        contact_person: selectedRestaurant.contact_person || '',
        logo_url: selectedRestaurant.logo_url || '',
        webhook_secret: selectedRestaurant.webhook_secret || '',
        api_key: selectedRestaurant.api_key || '',
        commission_rate: selectedRestaurant.commission_rate?.toString() || '',
        ubereats_commission_rate: selectedRestaurant.ubereats_commission_rate?.toString() || '',
        ubereats_markup: selectedRestaurant.ubereats_markup?.toString() || '',
        pickup_commission_rate: selectedRestaurant.pickup_commission_rate?.toString() || '',
        primary_color: selectedRestaurant.primary_color || '#4093ff',
        secondary_color: selectedRestaurant.secondary_color || '',
        stripe_customer_id: selectedRestaurant.stripe_customer_id || '',
        opt_in_checkbox_id: selectedRestaurant.opt_in_checkbox_id || '',
        implicit_consent_enabled: selectedRestaurant.implicit_consent_enabled || false,
        ordering_page_url: selectedRestaurant.ordering_page_url || '',
        review_page_url: selectedRestaurant.review_page_url || '',
        google_review_url: selectedRestaurant.google_review_url || '',
        facebook_url: selectedRestaurant.facebook_url || '',
        instagram_url: selectedRestaurant.instagram_url || ''
      };
      
      // Log the prepared form data
      console.log('Setting restaurant form data:', formData);
      
      // Update the form state
      setRestaurantForm(formData);
    }
  }, [selectedRestaurant]);
  
  // Set form data when selecting an organisation for editing
  useEffect(() => {
    if (selectedOrganisation) {
      // Log the data we received from the API
      console.log('Setting form with organisation data:', selectedOrganisation);
      
      // Prepare the form data with all fields, ensuring no nulls
      const formData = {
        name: selectedOrganisation.name || '',
        id: selectedOrganisation.id || '',
        stripe_customer_id: selectedOrganisation.stripe_customer_id || ''
      };
      
      // Log the prepared form data
      console.log('Setting organisation form data:', formData);
      
      // Update the form state
      setOrganisationForm(formData);
    }
  }, [selectedOrganisation]);
  
  // Calculate total pages
  const totalPages = Math.ceil(totalItems / pageSize);
  
  // Handle editing a restaurant - fetch complete restaurant data
  const handleEditRestaurant = async (restaurant: any) => {
    try {
      // Fetch complete restaurant data from API to ensure all fields are loaded
      const result = await apiClient.get(`/api/admin/restaurants/${restaurant.restaurant_id}`);
      
      if (result.data.success) {
        // Use the complete restaurant data from the API
        setSelectedRestaurant(result.data.restaurant);
      } else {
        // Fallback to the data from the table if API fails
        setSelectedRestaurant(restaurant);
        console.warn('Could not fetch complete restaurant data, using table data instead');
      }
    } catch (error) {
      console.error('Error fetching restaurant details:', error);
      // Fallback to the data from the table if API fails
      setSelectedRestaurant(restaurant);
    }
    
    setEditRestaurantDialogOpen(true);
  };
  
  // Handle editing an organisation - fetch complete organisation data
  const handleEditOrganisation = async (organisation: any) => {
    try {
      // Fetch complete organisation data from API to ensure all fields are loaded
      const result = await apiClient.get(`/api/admin/organisations/${organisation.id}`);
      
      if (result.data.success) {
        // Use the complete organisation data from the API
        setSelectedOrganisation(result.data.organisation);
      } else {
        // Fallback to the data from the table if API fails
        setSelectedOrganisation(organisation);
        console.warn('Could not fetch complete organisation data, using table data instead');
      }
    } catch (error) {
      console.error('Error fetching organisation details:', error);
      // Fallback to the data from the table if API fails
      setSelectedOrganisation(organisation);
    }
    
    setEditOrganisationDialogOpen(true);
  };
  
  // Handle saving restaurant changes
  const handleSaveRestaurantChanges = async () => {
    // Validate form
    const errors = {
      restaurant_name: !restaurantForm.restaurant_name ? 'Restaurant name is required' : '',
      restaurant_id: restaurantForm.restaurant_id && !/^[a-zA-Z0-9_\-]+$/.test(restaurantForm.restaurant_id) 
          ? 'Restaurant ID can only contain letters, numbers, underscores, and hyphens' 
          : ''
    };
    
    setRestaurantErrors(errors);
    
    // Check if we have any errors
    if (errors.restaurant_name || errors.restaurant_id) {
      return;
    }
    
    // If organisation_id is selected, fetch the organisation_name
    let organisation_name = null;
    if (restaurantForm.organisation_id) {
      try {
        const { data: org } = await supabase
          .from('organisations')
          .select('name')
          .eq('id', restaurantForm.organisation_id)
          .single();
          
        if (org) {
          organisation_name = org.name;
        }
      } catch (error) {
        console.error('Error fetching organisation name:', error);
      }
    }
    
    try {
      let result;
      
      // Prepare payload with organisation name if available
      const payload = {
        ...restaurantForm,
        organisation_name: organisation_name
      };
      
      // Parse numeric fields
      if (payload.commission_rate) payload.commission_rate = parseFloat(payload.commission_rate);
      if (payload.ubereats_commission_rate) payload.ubereats_commission_rate = parseFloat(payload.ubereats_commission_rate);
      if (payload.ubereats_markup) payload.ubereats_markup = parseFloat(payload.ubereats_markup);
      if (payload.pickup_commission_rate) payload.pickup_commission_rate = parseFloat(payload.pickup_commission_rate);
      
      // When editing a restaurant, log field comparisons to help with debugging
      if (selectedRestaurant) {
        console.log('Updating restaurant:', selectedRestaurant.restaurant_id);
        
        // Update existing restaurant
        result = await apiClient.put(`/api/admin/restaurants/${selectedRestaurant.restaurant_id}`, payload);
      } else {
        // Create new restaurant
        result = await apiClient.post('/api/admin/restaurants', payload);
      }
      
      if (result.data.success) {
        toast({
          title: selectedRestaurant ? 'Restaurant Updated' : 'Restaurant Created',
          description: `${restaurantForm.restaurant_name} has been ${selectedRestaurant ? 'updated' : 'created'} successfully.`,
        });
        
        // Close dialog and refresh data
        setEditRestaurantDialogOpen(false);
        setAddRestaurantDialogOpen(false);
        fetchData();
      } else {
        throw new Error(result.data.error || 'Operation failed');
      }
    } catch (err: any) {
      console.error('Error saving restaurant:', err);
      
      toast({
        title: 'Error',
        description: err.message || 'Failed to save restaurant',
        variant: 'destructive',
      });
    }
  };
  
  // Handle saving organisation changes
  const handleSaveOrganisationChanges = async () => {
    // Validate form
    const errors = {
      name: !organisationForm.name ? 'Organisation name is required' : '',
      id: organisationForm.id && !/^[a-zA-Z0-9_\-]+$/.test(organisationForm.id) 
          ? 'Organisation ID can only contain letters, numbers, underscores, and hyphens' 
          : ''
    };
    
    setOrganisationErrors(errors);
    
    // Check if we have any errors
    if (errors.name || errors.id) {
      return;
    }
    
    try {
      let result;
      
      if (selectedOrganisation) {
        // Update existing organisation
        result = await apiClient.put(`/api/admin/organisations/${selectedOrganisation.id}`, organisationForm);
      } else {
        // Create new organisation
        result = await apiClient.post('/api/admin/organisations', organisationForm);
      }
      
      if (result.data.success) {
        toast({
          title: selectedOrganisation ? 'Organisation Updated' : 'Organisation Created',
          description: `${organisationForm.name} has been ${selectedOrganisation ? 'updated' : 'created'} successfully.`,
        });
        
        // Close dialog and refresh data
        setEditOrganisationDialogOpen(false);
        setAddOrganisationDialogOpen(false);
        fetchData();
      } else {
        throw new Error(result.data.error || 'Operation failed');
      }
    } catch (err: any) {
      console.error('Error saving organisation:', err);
      
      toast({
        title: 'Error',
        description: err.message || 'Failed to save organisation',
        variant: 'destructive',
      });
    }
  };
  
  // Handle updating restaurant status
  const handleUpdateRestaurantStatus = async (restaurantId: string, newStatus: string) => {
    try {
      const result = await apiClient.patch(`/api/admin/restaurants/${restaurantId}/status`, { 
        status: newStatus 
      });
      
      if (result.data.success) {
        toast({
          title: 'Status Updated',
          description: `Restaurant status updated to ${newStatus}.`,
        });
        
        // Refresh data
        fetchData();
      } else {
        throw new Error(result.data.error || 'Failed to update status');
      }
    } catch (err: any) {
      console.error('Error updating status:', err);
      
      toast({
        title: 'Error',
        description: err.message || 'Failed to update status',
        variant: 'destructive',
      });
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-bold">Restaurants & Organisations</h2>
        
        <div className="flex w-full sm:w-auto gap-2 flex-wrap">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground/70" />
            <Input
              placeholder="Search..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          {activeTab === 'restaurants' && (
            <>
              <Select
                defaultValue="all"
                value={orgFilter}
                onValueChange={(value) => setOrgFilter(value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Organisations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Organisations</SelectItem>
                  <SelectItem value="none">No Organisation</SelectItem>
                  {initialOrganisations.map(org => (
                    <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select
                defaultValue="all"
                value={statusFilter}
                onValueChange={(value) => setStatusFilter(value)}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}
        </div>
      </div>
      
      <div className="mb-4 p-4 bg-muted rounded-md">
        <div className="flex items-start gap-2">
          <Info className="h-5 w-5 text-primary mt-0.5" />
          <div>
            <h3 className="font-medium mb-1">Organisation & Restaurant Structure</h3>
            <p className="text-sm text-muted-foreground">
              Restaurants belong to organisations in a hierarchical structure. Users with org_admin
              role can manage all restaurants within their assigned organisation, while rest_admin
              users are limited to specific restaurants.
            </p>
          </div>
        </div>
      </div>
      
      <Tabs defaultValue="restaurants" value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="restaurants">Restaurants</TabsTrigger>
          <TabsTrigger value="organisations">Organisations</TabsTrigger>
        </TabsList>
        
        <TabsContent value="restaurants" className="mt-4">
          <div className="flex justify-between mb-4">
            <div className="text-sm text-muted-foreground">
              {totalItems} restaurants found
            </div>
            <Dialog open={addRestaurantDialogOpen} onOpenChange={setAddRestaurantDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Add Restaurant
                </Button>
              </DialogTrigger>
              <ScrollableDialogContent className="sm:max-w-[600px]" aria-describedby="restaurant-form-description">
                <DialogHeader>
                  <DialogTitle>Add New Restaurant</DialogTitle>
                  <DialogDescription id="restaurant-form-description">
                    Enter details to create a new restaurant. Required fields are marked with an asterisk (*).
                  </DialogDescription>
                </DialogHeader>
                
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="restaurant_id" className="text-sm">
                        Restaurant ID
                      </Label>
                      <Input
                        id="restaurant_id"
                        placeholder="e.g., rest_saigonkingdom"
                        value={restaurantForm.restaurant_id}
                        onChange={(e) => setRestaurantForm({ ...restaurantForm, restaurant_id: e.target.value })}
                      />
                      {restaurantErrors.restaurant_id && (
                        <p className="text-sm text-destructive mt-1">{restaurantErrors.restaurant_id}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        Custom ID or leave blank for auto-generated
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="restaurant_name" className="text-sm">
                        Restaurant Name*
                      </Label>
                      <Input
                        id="restaurant_name"
                        value={restaurantForm.restaurant_name}
                        onChange={(e) => setRestaurantForm({ ...restaurantForm, restaurant_name: e.target.value })}
                      />
                      {restaurantErrors.restaurant_name && (
                        <p className="text-sm text-destructive mt-1">{restaurantErrors.restaurant_name}</p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="organisation_id" className="text-sm">
                      Organisation
                    </Label>
                    <Select
                      value={restaurantForm.organisation_id || "none"}
                      onValueChange={(value) => {
                        const newOrgId = value === "none" ? "" : value;
                        setRestaurantForm({ ...restaurantForm, organisation_id: newOrgId });
                      }}
                    >
                      <SelectTrigger id="organisation_id">
                        <SelectValue placeholder="No Organisation" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {initialOrganisations.map(org => (
                          <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="logo_url" className="text-sm">
                      Logo URL
                    </Label>
                    <Input
                      id="logo_url"
                      placeholder="https://example.com/logo.png"
                      value={restaurantForm.logo_url}
                      onChange={(e) => setRestaurantForm({ ...restaurantForm, logo_url: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Public URL to the restaurant logo image
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="stripe_customer_id" className="text-sm">
                      Stripe Customer ID
                    </Label>
                    <Input
                      id="stripe_customer_id"
                      placeholder="cus_xxxxxxxx"
                      value={restaurantForm.stripe_customer_id}
                      onChange={(e) => setRestaurantForm({ ...restaurantForm, stripe_customer_id: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Stripe customer ID for SMS billing (optional)
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="opt_in_checkbox_id" className="text-sm">
                      Marketing Opt-in Checkbox ID
                    </Label>
                    <Input
                      id="opt_in_checkbox_id"
                      placeholder="e.g., oSR52NcpL"
                      value={restaurantForm.opt_in_checkbox_id}
                      onChange={(e) => setRestaurantForm({ ...restaurantForm, opt_in_checkbox_id: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      ID of the checkout form checkbox used for marketing opt-in (optional)
                    </p>
                  </div>
                  
                  <div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="implicit_consent_enabled"
                        checked={restaurantForm.implicit_consent_enabled}
                        onCheckedChange={(checked) => setRestaurantForm({ ...restaurantForm, implicit_consent_enabled: checked })}
                      />
                      <Label htmlFor="implicit_consent_enabled" className="text-sm">
                        Enable implicit consent for marketing
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      When enabled, all customers will be automatically opted into marketing unless they have previously opted out from this organisation. Use this for restaurants that handle opt-outs externally.
                    </p>
                    <div className="flex items-start gap-2 p-3 bg-yellow-50 rounded-md mt-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5" />
                      <p className="text-xs text-yellow-800">
                        Important: Ensure your restaurant has appropriate consent collection and opt-out processes in place before enabling this feature.
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-2">
                    <h4 className="text-sm font-medium mb-2">Restaurant URLs</h4>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="ordering_page_url" className="text-sm">
                          Ordering Page URL
                        </Label>
                        <Input
                          id="ordering_page_url"
                          placeholder="https://example.com/order"
                          value={restaurantForm.ordering_page_url}
                          onChange={(e) => setRestaurantForm({ ...restaurantForm, ordering_page_url: e.target.value })}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="review_page_url" className="text-sm">
                          Review Page URL
                        </Label>
                        <Input
                          id="review_page_url"
                          placeholder="https://example.com/reviews"
                          value={restaurantForm.review_page_url}
                          onChange={(e) => setRestaurantForm({ ...restaurantForm, review_page_url: e.target.value })}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="google_review_url" className="text-sm">
                          Google Review URL
                        </Label>
                        <Input
                          id="google_review_url"
                          placeholder="https://g.page/r/..."
                          value={restaurantForm.google_review_url}
                          onChange={(e) => setRestaurantForm({ ...restaurantForm, google_review_url: e.target.value })}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="facebook_url" className="text-sm">
                          Facebook URL
                        </Label>
                        <Input
                          id="facebook_url"
                          placeholder="https://facebook.com/..."
                          value={restaurantForm.facebook_url}
                          onChange={(e) => setRestaurantForm({ ...restaurantForm, facebook_url: e.target.value })}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="instagram_url" className="text-sm">
                          Instagram URL
                        </Label>
                        <Input
                          id="instagram_url"
                          placeholder="https://instagram.com/..."
                          value={restaurantForm.instagram_url}
                          onChange={(e) => setRestaurantForm({ ...restaurantForm, instagram_url: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="webhook_secret" className="text-sm">
                      Webhook Secret
                    </Label>
                    <Input
                      id="webhook_secret"
                      placeholder="webhook_xxxxxxxx"
                      value={restaurantForm.webhook_secret}
                      onChange={(e) => setRestaurantForm({ ...restaurantForm, webhook_secret: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Secret key for webhook authentication
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="api_key" className="text-sm">
                      Ordering Dashboard API Key
                    </Label>
                    <Input
                      id="api_key"
                      placeholder="api_xxxxxxxx"
                      value={restaurantForm.api_key}
                      onChange={(e) => setRestaurantForm({ ...restaurantForm, api_key: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      API key for Cloudwaitress ordering dashboard integration
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="address" className="text-sm">
                      Address
                    </Label>
                    <Textarea
                      id="address"
                      rows={2}
                      value={restaurantForm.address}
                      onChange={(e) => setRestaurantForm({ ...restaurantForm, address: e.target.value })}
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email" className="text-sm">
                        Email
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={restaurantForm.email}
                        onChange={(e) => setRestaurantForm({ ...restaurantForm, email: e.target.value })}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="phone" className="text-sm">
                        Phone
                      </Label>
                      <Input
                        id="phone"
                        value={restaurantForm.phone}
                        onChange={(e) => setRestaurantForm({ ...restaurantForm, phone: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="status" className="text-sm">
                        Status
                      </Label>
                      <Select
                        value={restaurantForm.status}
                        onValueChange={(value) => setRestaurantForm({ ...restaurantForm, status: value })}
                      >
                        <SelectTrigger id="status">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="contact_person" className="text-sm">
                        Contact Person
                      </Label>
                      <Input
                        id="contact_person"
                        value={restaurantForm.contact_person}
                        onChange={(e) => setRestaurantForm({ ...restaurantForm, contact_person: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div className="mt-2">
                    <h4 className="text-sm font-medium mb-2">Commission Rates</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="commission_rate" className="text-sm">
                          Commission Rate (%)
                        </Label>
                        <Input
                          id="commission_rate"
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={restaurantForm.commission_rate}
                          onChange={(e) => setRestaurantForm({ ...restaurantForm, commission_rate: e.target.value })}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="ubereats_commission_rate" className="text-sm">
                          UberEats Commission Rate (%)
                        </Label>
                        <Input
                          id="ubereats_commission_rate"
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={restaurantForm.ubereats_commission_rate}
                          onChange={(e) => setRestaurantForm({ ...restaurantForm, ubereats_commission_rate: e.target.value })}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="ubereats_markup" className="text-sm">
                          UberEats Markup (%)
                        </Label>
                        <Input
                          id="ubereats_markup"
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={restaurantForm.ubereats_markup}
                          onChange={(e) => setRestaurantForm({ ...restaurantForm, ubereats_markup: e.target.value })}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="pickup_commission_rate" className="text-sm">
                          Pickup Commission Rate (%)
                        </Label>
                        <Input
                          id="pickup_commission_rate"
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={restaurantForm.pickup_commission_rate}
                          onChange={(e) => setRestaurantForm({ ...restaurantForm, pickup_commission_rate: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-2">
                    <h4 className="text-sm font-medium mb-2">Branding Colors</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="primary_color" className="text-sm">
                          Primary Color
                        </Label>
                        <div className="flex gap-2">
                          <div 
                            className="w-8 h-8 rounded-md border" 
                            style={{ backgroundColor: restaurantForm.primary_color || '#4093ff' }}
                          />
                          <Input
                            id="primary_color"
                            type="text"
                            placeholder="#4093ff"
                            value={restaurantForm.primary_color}
                            onChange={(e) => setRestaurantForm({ ...restaurantForm, primary_color: e.target.value })}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="secondary_color" className="text-sm">
                          Secondary Color
                        </Label>
                        <div className="flex gap-2">
                          <div 
                            className="w-8 h-8 rounded-md border" 
                            style={{ backgroundColor: restaurantForm.secondary_color || '' }}
                          />
                          <Input
                            id="secondary_color"
                            type="text"
                            placeholder="#000000"
                            value={restaurantForm.secondary_color}
                            onChange={(e) => setRestaurantForm({ ...restaurantForm, secondary_color: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setAddRestaurantDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveRestaurantChanges}>
                    Create Restaurant
                  </Button>
                </DialogFooter>
              </ScrollableDialogContent>
            </Dialog>
          </div>
          
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Restaurant Name</TableHead>
                    <TableHead>Organisation</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array(5).fill(0).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-6 w-32" /></TableCell>
                        <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                        <TableCell><Skeleton className="h-6 w-48" /></TableCell>
                        <TableCell className="text-right"><Skeleton className="h-6 w-8 ml-auto" /></TableCell>
                      </TableRow>
                    ))
                  ) : error ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-4 text-destructive">
                        Error loading restaurants: {error}
                      </TableCell>
                    </TableRow>
                  ) : restaurants.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-4">
                        No restaurants found
                      </TableCell>
                    </TableRow>
                  ) : (
                    restaurants.map((restaurant) => (
                      <TableRow key={restaurant.restaurant_id} className="group">
                        <TableCell>
                          <button
                            onClick={() => handleEditRestaurant(restaurant)}
                            className="flex items-center font-medium cursor-pointer transition-colors hover:text-primary active:text-primary"
                          >
                            {restaurant.organisation_id && (
                              <div className="w-2 h-2 bg-primary rounded-full mr-2" />
                            )}
                            {restaurant.restaurant_name}
                          </button>
                        </TableCell>
                        <TableCell>
                          {restaurant.organisation_id ? (
                            <div className="flex items-center">
                              <span>{restaurant.organisation_name}</span>
                              <Badge variant="outline" className="ml-2">
                                {restaurant.organisation_id}
                              </Badge>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">No organisation</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={restaurant.status === 'active'}
                              onCheckedChange={(checked) => {
                                handleUpdateRestaurantStatus(
                                  restaurant.restaurant_id,
                                  checked ? 'active' : 'inactive'
                                );
                              }}
                            />
                            <Badge variant={restaurant.status === 'active' ? 'default' : 'secondary'}>
                              {restaurant.status || 'active'}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>{restaurant.address || '—'}</TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleEditRestaurant(restaurant)}
                            className="opacity-0 group-hover:opacity-100"
                          >
                            <Edit className="h-4 w-4" />
                            <span className="sr-only">Edit</span>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              
              {/* Pagination controls */}
              {!isLoading && !error && restaurants.length > 0 && (
                <div className="flex items-center justify-between px-4 py-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {restaurants.length > 0 ? (page - 1) * pageSize + 1 : 0} - {Math.min(page * pageSize, totalItems)} of {totalItems} restaurants
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="text-sm">
                      Page {page} of {totalPages || 1}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Edit Restaurant Dialog */}
          <Dialog open={editRestaurantDialogOpen} onOpenChange={setEditRestaurantDialogOpen}>
            <ScrollableDialogContent className="sm:max-w-[600px]" aria-describedby="edit-restaurant-description">
              <DialogHeader>
                <DialogTitle>Edit Restaurant</DialogTitle>
                <DialogDescription id="edit-restaurant-description">
                  Update restaurant details. Required fields are marked with an asterisk (*).
                </DialogDescription>
              </DialogHeader>
              
              {selectedRestaurant && (
                <div className="grid gap-4 py-4">
                  <div className="p-3 bg-muted rounded-md mb-2">
                    <div className="flex items-start gap-2">
                      <Info className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Restaurant ID: <span className="font-mono">{selectedRestaurant.restaurant_id}</span>
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Restaurant IDs cannot be changed after creation.
                        </p>
                      </div>
                    </div>
                  </div>
                
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit_restaurant_name" className="text-sm">
                        Restaurant Name*
                      </Label>
                      <Input
                        id="edit_restaurant_name"
                        value={restaurantForm.restaurant_name}
                        onChange={(e) => setRestaurantForm({ ...restaurantForm, restaurant_name: e.target.value })}
                      />
                      {restaurantErrors.restaurant_name && (
                        <p className="text-sm text-destructive mt-1">{restaurantErrors.restaurant_name}</p>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="edit_organisation_id" className="text-sm">
                        Organisation
                      </Label>
                      <Select
                        value={restaurantForm.organisation_id || "none"}
                        onValueChange={(value) => {
                          const newOrgId = value === "none" ? "" : value;
                          setRestaurantForm({ ...restaurantForm, organisation_id: newOrgId });
                        }}
                      >
                        <SelectTrigger id="edit_organisation_id">
                          <SelectValue placeholder="No Organisation" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {initialOrganisations.map(org => (
                            <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="edit_logo_url" className="text-sm">
                      Logo URL
                    </Label>
                    <Input
                      id="edit_logo_url"
                      placeholder="https://example.com/logo.png"
                      value={restaurantForm.logo_url}
                      onChange={(e) => setRestaurantForm({ ...restaurantForm, logo_url: e.target.value })}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="edit_stripe_customer_id" className="text-sm">
                      Stripe Customer ID
                    </Label>
                    <Input
                      id="edit_stripe_customer_id"
                      placeholder="cus_xxxxxxxx"
                      value={restaurantForm.stripe_customer_id}
                      onChange={(e) => setRestaurantForm({ ...restaurantForm, stripe_customer_id: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Stripe customer ID for SMS billing (optional)
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="edit_opt_in_checkbox_id" className="text-sm">
                      Marketing Opt-in Checkbox ID
                    </Label>
                    <Input
                      id="edit_opt_in_checkbox_id"
                      placeholder="e.g., oSR52NcpL"
                      value={restaurantForm.opt_in_checkbox_id}
                      onChange={(e) => setRestaurantForm({ ...restaurantForm, opt_in_checkbox_id: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      ID of the checkout form checkbox used for marketing opt-in (optional)
                    </p>
                  </div>
                  
                  <div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="edit_implicit_consent_enabled"
                        checked={restaurantForm.implicit_consent_enabled}
                        onCheckedChange={(checked) => setRestaurantForm({ ...restaurantForm, implicit_consent_enabled: checked })}
                      />
                      <Label htmlFor="edit_implicit_consent_enabled" className="text-sm">
                        Enable implicit consent for marketing
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      When enabled, all customers will be automatically opted into marketing unless they have previously opted out from this organisation. Use this for restaurants that handle opt-outs externally.
                    </p>
                    <div className="flex items-start gap-2 p-3 bg-yellow-50 rounded-md mt-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5" />
                      <p className="text-xs text-yellow-800">
                        Important: Ensure your restaurant has appropriate consent collection and opt-out processes in place before enabling this feature.
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-2">
                    <h4 className="text-sm font-medium mb-2">Restaurant URLs</h4>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="edit_ordering_page_url" className="text-sm">
                          Ordering Page URL
                        </Label>
                        <Input
                          id="edit_ordering_page_url"
                          placeholder="https://example.com/order"
                          value={restaurantForm.ordering_page_url}
                          onChange={(e) => setRestaurantForm({ ...restaurantForm, ordering_page_url: e.target.value })}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="edit_review_page_url" className="text-sm">
                          Review Page URL
                        </Label>
                        <Input
                          id="edit_review_page_url"
                          placeholder="https://example.com/reviews"
                          value={restaurantForm.review_page_url}
                          onChange={(e) => setRestaurantForm({ ...restaurantForm, review_page_url: e.target.value })}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="edit_google_review_url" className="text-sm">
                          Google Review URL
                        </Label>
                        <Input
                          id="edit_google_review_url"
                          placeholder="https://g.page/r/..."
                          value={restaurantForm.google_review_url}
                          onChange={(e) => setRestaurantForm({ ...restaurantForm, google_review_url: e.target.value })}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="edit_facebook_url" className="text-sm">
                          Facebook URL
                        </Label>
                        <Input
                          id="edit_facebook_url"
                          placeholder="https://facebook.com/..."
                          value={restaurantForm.facebook_url}
                          onChange={(e) => setRestaurantForm({ ...restaurantForm, facebook_url: e.target.value })}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="edit_instagram_url" className="text-sm">
                          Instagram URL
                        </Label>
                        <Input
                          id="edit_instagram_url"
                          placeholder="https://instagram.com/..."
                          value={restaurantForm.instagram_url}
                          onChange={(e) => setRestaurantForm({ ...restaurantForm, instagram_url: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="edit_webhook_secret" className="text-sm">
                      Webhook Secret
                    </Label>
                    <Input
                      id="edit_webhook_secret"
                      placeholder="webhook_xxxxxxxx"
                      value={restaurantForm.webhook_secret}
                      onChange={(e) => setRestaurantForm({ ...restaurantForm, webhook_secret: e.target.value })}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="edit_api_key" className="text-sm">
                      Ordering Dashboard API Key
                    </Label>
                    <Input
                      id="edit_api_key"
                      placeholder="api_xxxxxxxx"
                      value={restaurantForm.api_key}
                      onChange={(e) => setRestaurantForm({ ...restaurantForm, api_key: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      API key for Cloudwaitress ordering dashboard integration
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="edit_address" className="text-sm">
                      Address
                    </Label>
                    <Textarea
                      id="edit_address"
                      rows={2}
                      value={restaurantForm.address}
                      onChange={(e) => setRestaurantForm({ ...restaurantForm, address: e.target.value })}
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit_email" className="text-sm">
                        Email
                      </Label>
                      <Input
                        id="edit_email"
                        type="email"
                        value={restaurantForm.email}
                        onChange={(e) => setRestaurantForm({ ...restaurantForm, email: e.target.value })}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="edit_phone" className="text-sm">
                        Phone
                      </Label>
                      <Input
                        id="edit_phone"
                        value={restaurantForm.phone}
                        onChange={(e) => setRestaurantForm({ ...restaurantForm, phone: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit_status" className="text-sm">
                        Status
                      </Label>
                      <Select
                        value={restaurantForm.status}
                        onValueChange={(value) => setRestaurantForm({ ...restaurantForm, status: value })}
                      >
                        <SelectTrigger id="edit_status">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="edit_contact_person" className="text-sm">
                        Contact Person
                      </Label>
                      <Input
                        id="edit_contact_person"
                        value={restaurantForm.contact_person}
                        onChange={(e) => setRestaurantForm({ ...restaurantForm, contact_person: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div className="mt-2">
                    <h4 className="text-sm font-medium mb-2">Commission Rates</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="edit_commission_rate" className="text-sm">
                          Commission Rate (%)
                        </Label>
                        <Input
                          id="edit_commission_rate"
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={restaurantForm.commission_rate}
                          onChange={(e) => setRestaurantForm({ ...restaurantForm, commission_rate: e.target.value })}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="edit_ubereats_commission_rate" className="text-sm">
                          UberEats Commission Rate (%)
                        </Label>
                        <Input
                          id="edit_ubereats_commission_rate"
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={restaurantForm.ubereats_commission_rate}
                          onChange={(e) => setRestaurantForm({ ...restaurantForm, ubereats_commission_rate: e.target.value })}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="edit_ubereats_markup" className="text-sm">
                          UberEats Markup (%)
                        </Label>
                        <Input
                          id="edit_ubereats_markup"
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={restaurantForm.ubereats_markup}
                          onChange={(e) => setRestaurantForm({ ...restaurantForm, ubereats_markup: e.target.value })}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="edit_pickup_commission_rate" className="text-sm">
                          Pickup Commission Rate (%)
                        </Label>
                        <Input
                          id="edit_pickup_commission_rate"
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={restaurantForm.pickup_commission_rate}
                          onChange={(e) => setRestaurantForm({ ...restaurantForm, pickup_commission_rate: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-2">
                    <h4 className="text-sm font-medium mb-2">Branding Colors</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="edit_primary_color" className="text-sm">
                          Primary Color
                        </Label>
                        <div className="flex gap-2">
                          <div 
                            className="w-8 h-8 rounded-md border" 
                            style={{ backgroundColor: restaurantForm.primary_color || '#4093ff' }}
                          />
                          <Input
                            id="edit_primary_color"
                            type="text"
                            placeholder="#4093ff"
                            value={restaurantForm.primary_color}
                            onChange={(e) => setRestaurantForm({ ...restaurantForm, primary_color: e.target.value })}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="edit_secondary_color" className="text-sm">
                          Secondary Color
                        </Label>
                        <div className="flex gap-2">
                          <div 
                            className="w-8 h-8 rounded-md border" 
                            style={{ backgroundColor: restaurantForm.secondary_color || '' }}
                          />
                          <Input
                            id="edit_secondary_color"
                            type="text"
                            placeholder="#000000"
                            value={restaurantForm.secondary_color}
                            onChange={(e) => setRestaurantForm({ ...restaurantForm, secondary_color: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditRestaurantDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveRestaurantChanges}>
                  Save Changes
                </Button>
              </DialogFooter>
            </ScrollableDialogContent>
          </Dialog>
        </TabsContent>
        
        <TabsContent value="organisations" className="mt-4">
          <div className="flex justify-between mb-4">
            <div className="text-sm text-muted-foreground">
              {totalItems} organisations found
            </div>
            <Dialog open={addOrganisationDialogOpen} onOpenChange={setAddOrganisationDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Add Organisation
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]" aria-describedby="add-org-description">
                <DialogHeader>
                  <DialogTitle>Add New Organisation</DialogTitle>
                  <DialogDescription id="add-org-description">
                    Enter details for the new organisation. Required fields are marked with an asterisk (*).
                  </DialogDescription>
                </DialogHeader>
                
                <div className="grid gap-4 py-4">
                  <div>
                    <Label htmlFor="organisation_name" className="text-sm">
                      Organisation Name*
                    </Label>
                    <Input
                      id="organisation_name"
                      value={organisationForm.name}
                      onChange={(e) => setOrganisationForm({ ...organisationForm, name: e.target.value })}
                    />
                    {organisationErrors.name && (
                      <p className="text-sm text-destructive mt-1">{organisationErrors.name}</p>
                    )}
                  </div>
                  
                  <div>
                    <Label htmlFor="organisation_id" className="text-sm">
                      Organisation ID*
                    </Label>
                    <Input
                      id="organisation_id"
                      value={organisationForm.id || ''}
                      placeholder="e.g., org_company1"
                      onChange={(e) => setOrganisationForm({ ...organisationForm, id: e.target.value })}
                    />
                    {organisationErrors.id && (
                      <p className="text-sm text-destructive mt-1">{organisationErrors.id}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      Provide a unique identifier for this organisation. If left empty, an ID will be auto-generated.
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="org_stripe_customer_id" className="text-sm">
                      Stripe Customer ID
                    </Label>
                    <Input
                      id="org_stripe_customer_id"
                      placeholder="cus_xxxxxxxx"
                      value={organisationForm.stripe_customer_id}
                      onChange={(e) => setOrganisationForm({ ...organisationForm, stripe_customer_id: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Stripe customer ID for SMS billing (optional)
                    </p>
                  </div>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setAddOrganisationDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveOrganisationChanges}>
                    Create Organisation
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organisation Name</TableHead>
                    <TableHead>ID</TableHead>
                    <TableHead>Restaurant Count</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array(5).fill(0).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-6 w-32" /></TableCell>
                        <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                        <TableCell className="text-right"><Skeleton className="h-6 w-8 ml-auto" /></TableCell>
                      </TableRow>
                    ))
                  ) : error ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-4 text-destructive">
                        Error loading organisations: {error}
                      </TableCell>
                    </TableRow>
                  ) : organisations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-4">
                        No organisations found
                      </TableCell>
                    </TableRow>
                  ) : (
                    organisations.map((org) => (
                      <TableRow key={org.id} className="group">
                        <TableCell>
                          <button
                            onClick={() => handleEditOrganisation(org)}
                            className="font-medium cursor-pointer transition-colors hover:text-primary active:text-primary"
                          >
                            {org.name}
                          </button>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {org.id}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span>{org.restaurant_count || 0} restaurants</span>
                            {org.restaurant_count > 0 && (
                              <span className="text-xs text-muted-foreground">
                                {org.active_count || 0} active
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleEditOrganisation(org)}
                            className="opacity-0 group-hover:opacity-100"
                          >
                            <Edit className="h-4 w-4" />
                            <span className="sr-only">Edit</span>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              
              {/* Pagination controls */}
              {!isLoading && !error && organisations.length > 0 && (
                <div className="flex items-center justify-between px-4 py-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {organisations.length > 0 ? (page - 1) * pageSize + 1 : 0} - {Math.min(page * pageSize, totalItems)} of {totalItems} organisations
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="text-sm">
                      Page {page} of {totalPages || 1}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Edit Organisation Dialog */}
          <Dialog open={editOrganisationDialogOpen} onOpenChange={setEditOrganisationDialogOpen}>
            <DialogContent className="sm:max-w-[425px]" aria-describedby="edit-org-description">
              <DialogHeader>
                <DialogTitle>Edit Organisation</DialogTitle>
                <DialogDescription id="edit-org-description">
                  Update the organisation details.
                </DialogDescription>
              </DialogHeader>
              
              {selectedOrganisation && (
                <div className="grid gap-4 py-4">
                  <div>
                    <Label htmlFor="edit_organisation_name" className="text-sm">
                      Organisation Name*
                    </Label>
                    <Input
                      id="edit_organisation_name"
                      value={organisationForm.name}
                      onChange={(e) => setOrganisationForm({ ...organisationForm, name: e.target.value })}
                    />
                    {organisationErrors.name && (
                      <p className="text-sm text-destructive mt-1">{organisationErrors.name}</p>
                    )}
                  </div>
                  
                  <div className="p-3 bg-muted rounded-md">
                    <div className="flex items-start gap-2">
                      <Info className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Organisation ID: <span className="font-mono">{selectedOrganisation.id}</span>
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Organisation IDs cannot be changed after creation.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="edit_org_stripe_customer_id" className="text-sm">
                      Stripe Customer ID
                    </Label>
                    <Input
                      id="edit_org_stripe_customer_id"
                      placeholder="cus_xxxxxxxx"
                      value={organisationForm.stripe_customer_id}
                      onChange={(e) => setOrganisationForm({ ...organisationForm, stripe_customer_id: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Stripe customer ID for SMS billing (optional)
                    </p>
                  </div>
                </div>
              )}
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditOrganisationDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveOrganisationChanges}>
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SuperAdminRestaurantsTab;